/**
 * @page ChangePasswordPage
 * @description 首次登入或強制修改密碼頁面
 * 流程：輸入目前密碼 + 新密碼 + 確認 → POST /auth/change-password → 導向首頁
 */

import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/api';
import { useSiteName } from '../../hooks/useSiteName';

// ============================================================
// Schema
// ============================================================
function createChangePasswordSchema(t: (key: string) => string) {
  return z
    .object({
      currentPassword: z.string().min(1, t('passwordRequired')),
      newPassword: z
        .string()
        .min(8, t('passwordMinLength'))
        .regex(/[a-z]/, t('passwordLowercase'))
        .regex(/[A-Z]/, t('passwordUppercase'))
        .regex(/[\d\W]/, t('passwordNumberOrSpecial')),
      confirmPassword: z.string().min(1, t('confirmPasswordRequired')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('passwordsDoNotMatch'),
      path: ['confirmPassword'],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message:
        t('newPasswordMustDiffer') || 'New password must be different from current password.',
      path: ['newPassword'],
    });
}

type ChangePasswordFormData = z.infer<ReturnType<typeof createChangePasswordSchema>>;

// ============================================================
// API
// ============================================================
async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiClient.post('/auth/change-password', { currentPassword, newPassword });
}

// ============================================================
// Component
// ============================================================
export default function ChangePasswordPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const siteName = useSiteName();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(createChangePasswordSchema(t)),
  });

  const mutation = useMutation({
    mutationFn: (data: ChangePasswordFormData) =>
      changePassword(data.currentPassword, data.newPassword),
    onSuccess: () => {
      navigate('/');
    },
    onError: (error: Error) => {
      const status = (error as { response?: { status?: number } }).response?.status;
      setError('currentPassword', {
        message: status === 401 ? t('currentPasswordIncorrect') : 'Server error. Please try again.',
      });
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100">
      <div className="w-full max-w-md px-6">
        <div className="text-center mb-8">
          <h1 className="heading-font text-3xl font-bold text-primary">{siteName}</h1>
          <p className="text-base-content/60 mt-2 text-sm">{t('mustChangePassword')}</p>
        </div>

        <div className="bg-base-200 rounded-2xl p-8 shadow-lg border border-base-300">
          <h2 className="text-xl font-semibold text-base-content mb-2">{t('changePassword')}</h2>
          <p className="text-sm text-base-content/60 mb-5">{t('changePasswordHint')}</p>

          <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
            {/* Current Password */}
            <div>
              <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                {t('currentPassword')}
              </label>
              <input
                {...register('currentPassword')}
                type="password"
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
              />
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-error">{errors.currentPassword.message}</p>
              )}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                {t('newPassword')}
              </label>
              <input
                {...register('newPassword')}
                type="password"
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
              />
              {errors.newPassword && (
                <p className="mt-1 text-sm text-error">{errors.newPassword.message}</p>
              )}
              <p className="mt-1 text-xs text-base-content/40">{t('passwordRules')}</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-base-content/80 mb-1.5">
                {t('confirmPassword')}
              </label>
              <input
                {...register('confirmPassword')}
                type="password"
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-lg bg-base-300 border border-base-300 text-base-content focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary transition"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-error">{errors.confirmPassword.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full py-3 rounded-lg bg-primary text-primary-content font-semibold hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? t('changingPassword') : t('changePassword')}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-base-content/30 mt-6">{t('version')}</p>
      </div>
    </div>
  );
}
